import type { ControlTransitionDefinitionMapping, FormDefinitionMapping, FormInput } from "@wtg-glow/form-flow-engine";
import type { ResourceInfo } from "CaptionService";
import type { DeferredPromise } from "DeferredPromise";
import type { EntitySaveResult } from "EntitySaveService";
import type { FormFlowOthersViewModel } from "FormFlowOthersModel";
import type {
  ControlTransitionEventArgs,
  PendingChangesHandler,
  ShellDisposeFn,
  ShellProviderFn,
  SubFormFlowEventArgs,
} from "FormFlowTypes";
import type MessageBus from "MessageBus";
import type ModelProvider from "ModelProvider";
import type NotificationSummary from "NotificationSummary";
import type { TaskViewModelTitle } from "TaskViewModel";
import type { UIContextOptions } from "UIService";
import type { WorkflowContext } from "VueHooks/WorkflowContextHook";
import type { Entity } from "breeze-client";
import { type Observable, type PureComputed, type Subscribable } from "knockout";
import { type WtgFrameworkActionMenuItem, type WtgFrameworkTaskGenericActionPlacement } from "wtg-material-ui";

/*! StartNoStringValidationRegion No captions*/
export enum FormAction {
  EDocs = "EDocs",
  Messages = "Messages",
  Notes = "Notes",
  Logs = "Logs",
  Workflows = "Workflows",
  Documents = "Documents",
}
/*! EndNoStringValidationRegion */

export type FormEntity = FormInput<keyof FormDefinitionMapping> | Entity;

export type FormOptions = Readonly<{
  formId: keyof FormDefinitionMapping;
  title?: string;
  formEntity?: FormEntity | Observable<FormEntity | undefined>;
  disabledFormActions?: FormAction[];
  buttonConfig?: {
    hideFooter?: boolean;
    hideSaveButtons?: boolean;
    hideCloseButtons?: boolean;
    hideBackButtons?: boolean;
    /** Only show close button */
    disableSaveCancel?: boolean;
    /** Footer transitions for the form */
    footerTransitions?: FormFooterTransition[] | Subscribable<FormFooterTransition[]>;
  };
  /** Control transitions for the form */
  controlTransitions?: FormControlTransition[];
  handleSubFormFlowAsync?: (eventArgs: SubFormFlowEventArgs) => Promise<FormTransitionResult | undefined>;
  debugLogId?: string;
}>;

/** Additional options for loading the UI shell. */
export type ShellLoaderOptions = {
  readonly shellProviderFn: ShellProviderFn;
  readonly shellDisposeFn: ShellDisposeFn;
};

export type FormTransitionResult = {
  remainOpen?: boolean;
};

/* In this type some of the properties are purposefully left ResourceInfo.
 * G1 forms require this to be ResourceInfo.
 * G2 forms require this to be String, which is handled at TaskHook.ts.
 * Coded form-flows, which will use G2 forms will pass Subscribable<string>.
 */
export type FormFooterTransition = {
  footerTransitionId: string;
  Caption: string | Subscribable<string> | ResourceInfo;
  Category?: string | Subscribable<string> | ResourceInfo;
  Icon?: string;
  Placement?: WtgFrameworkTaskGenericActionPlacement;
  SuspendValidation?: boolean;
  IsDisabled?: boolean | Subscribable<boolean>;
  Tooltip?: string | Subscribable<string> | ResourceInfo;
  onTransitionAsync: () => Promise<FormTransitionResult | undefined>;
};

export type FormControlTransition = {
  controlId: ControlTransitionDefinitionMapping<keyof FormDefinitionMapping>;
  SuspendValidation?: boolean;
  onTransitionAsync: (eventArgs: ControlTransitionEventArgs) => Promise<FormTransitionResult | undefined>;
};

export type FormFlowUIMenuOptions = {
  canShowEDocs: PureComputed<boolean>;
  canShowLogs: PureComputed<boolean>;
  canShowMessages: boolean;
  canShowNotes: PureComputed<boolean>;
  canShowWorkflow: PureComputed<boolean>;
  canShowDocuments: PureComputed<boolean>;
  showEDocsAsync: () => Promise<void>;
  showLogsAsync: () => Promise<void>;
  showMessagesAsync: () => Promise<void>;
  showNotesAsync: () => Promise<void>;
  getDocumentMenuItemsAsync: () => Promise<WtgFrameworkActionMenuItem[]>;
};

export type FormFlowUI = {
  readonly formId: keyof FormDefinitionMapping;
  readonly title: string | TaskViewModelTitle;
  readonly entity: Subscribable<FormEntity | undefined> | undefined;
  readonly entityUrl: PureComputed<string | undefined> | undefined;
  readonly isFavorite: Subscribable<boolean> | undefined;
  readonly isBusy: Observable<boolean>;
  readonly notificationSummary: NotificationSummary;
  readonly workflowContext: WorkflowContext;
  readonly pendingActionHandlers: Set<PendingChangesHandler>;
  readonly hasChanges: Subscribable<boolean>;
  readonly semaphoreTokens: Map<string, string>;
  readonly disabledFormActions?: FormAction[];
  readonly modelProvider?: ModelProvider;
  readonly others?: FormFlowOthersViewModel;
  readonly buttonConfig?: Readonly<{
    hideFooter?: boolean;
    hideSaveButtons?: boolean;
    hideCloseButtons?: boolean;
    hideBackButtons?: boolean;
    /** Only show close button */
    disableSaveCancel?: boolean;
    /** Footer transitions for the form */
    footerTransitions?: Subscribable<FormFooterTransition[]>;
  }>;
  readonly clientPK?: string;
  readonly uiContextOptions?: UIContextOptions;
  readonly messageBus: MessageBus;

  isAborted: boolean;
  $shell?: JQuery<Element>;
  formBindingContext?: ko.BindingContext;
  formAwaiter: DeferredPromise<void>;
  transitionAwaiter?: DeferredPromise<void>;

  shellProviderFn: ShellProviderFn;
  shellDisposeFn: ShellDisposeFn;
  getAllEntities(): Entity[];
  abort(): void;
  ensureNotAborted(): void | never;
  /** Discard any changes tracked so far. */
  discardChangesAsync(): Promise<void>;
  /** Callback to save the changes. Use EntitySaveService to save any changes and return the result. */
  saveChangesAsync(shouldRefresh: boolean): Promise<EntitySaveResult>;
  reloadEntityAsync: () => Promise<void>;
  getControlTransition(controlId: string): FormControlTransition | undefined;
  handleSubFormFlowAsync?: (eventArgs: SubFormFlowEventArgs) => Promise<FormTransitionResult | undefined>;
  log(message: string): void;
  toggleFavoriteAsync?(): Promise<void>;
};

export type ExtendedFormFlowUI = FormFlowUI & { configurationParentID?: string; clientPK?: string };
